import { IsArray, IsBoolean, IsEnum, IsObject, IsOptional, IsString, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { LayerTextDto } from './layer-text.dto';
import { AnimationDto } from '../animation.dto';
import { VisibilityConditionDto } from '../visibility-condition.dto';
import { AssetDto } from '../../../asset/models/asset.dto';
import { LayerVideoDto } from './layer-video.dto';
import { TimingConfigDto } from '../timing.dto';
import { LayerAudioDto } from './layer-audio.dto';
import { LayerOptimizationsDto } from './layer-optimization.dto';
import { InteractivityDto } from '../interactivity.dto';
import { MergeTagDto } from "../merge-tag/merge-tag.dto";
import { LayerHtmlDto } from './layer-html.dto';
import { LayerButtonDto } from './layer-button.dto';
import { LayerShapeDto } from './layer-shape.dto';
import { ActionsDto } from '../action.dto';
import { LayoutGroupModel } from './layout-group.dto';

export enum LayerType {
	TEXT = 'text',
	HTML = 'html',
	IMAGE = 'image',
	VIDEO = 'video',
	AUDIO = 'audio',
	BUTTON = 'button',
	SHAPE = 'shape',
	GROUP = 'group',
	SELECTION_GROUP = 'selectionGroup',
	MOCK = 'mock'
}

// Purpose options for text layers
export const TEXT_LAYER_PURPOSES = [
  'Headline',
  'Subline',
  'Benefit',
  'Address',
  'Legal',
  'Offer',
  'Call to Action'
] as const;

// Purpose options for button layers
export const BUTTON_LAYER_PURPOSES = [
  'Call to Action',
  'Label'
] as const;

// Purpose options for image layers
export const IMAGE_LAYER_PURPOSES = [
  'Logo',
  'Product Shot',
  'Photography'
] as const;

export interface ImageCropData {
	enabled: boolean;
	// Crop area (visible portion on canvas)
	// Optional: when only `enabled: true` is set, actual geometry is calculated from DOM
	cropBox?: {
	  x: number;        // px from layer origin
	  y: number;        // px from layer origin
	  width: number;    // px
	  height: number;   // px
	};
	// Image positioning within crop area
	// Optional: when only `enabled: true` is set, actual geometry is calculated from DOM
	image?: {
	  x: number;        // px offset within crop box
	  y: number;        // px offset within crop box
	  width: number;    // px (can be scaled)
	  height: number;   // px (can be scaled)
	  scale: number;    // scale factor (1 = 100%)
	};
}

export interface LayerDuplicateOptions {
	withStyles?: boolean;
	withProperties?: boolean;
	customOverrides?: Record<string, any>;
	idsMap?: {[key: string]: string}; // original ID -> new ID
}

export type TextLayerPurpose = typeof TEXT_LAYER_PURPOSES[number];
export type ImageLayerPurpose = typeof IMAGE_LAYER_PURPOSES[number];
export type ButtonLayerPurpose = typeof BUTTON_LAYER_PURPOSES[number];

export class LayerGroupDto {
	@IsString()
	@IsOptional()
	flow?: 'freeform' | 'legacy';

	@IsOptional()
	@IsObject()
	layout?: LayoutGroupModel;
}

export class LayerCTAOptionsDto {
	@IsString()
	url: string;

	@IsString()
	@IsOptional()
	target?: string; // _blank | _self | _parent | _top | custom_frame_name
}

export class LayerDto {
	@IsString()
	id: string;

	// This should be added only when a layer is unit specific (if layer is not a linked/package layer)
	@IsString()
	@IsOptional()
	creativeUnitId?: string; // The associated creative unit ID for this layer.

	@IsString()
	@IsOptional()
	label: string;

	@IsEnum(LayerType)
	type: LayerType;

	@IsString()
	@IsOptional()
	classes?: string;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerTextDto)
	text?: LayerTextDto;

	@IsOptional()
	html?: LayerHtmlDto;

	@IsOptional()
	image?: AssetDto;

	@IsOptional()
	@Type(() => LayerVideoDto)
	video?: LayerVideoDto;

	@IsOptional()
	audio?: AssetDto;

	@IsOptional()
	button?: LayerButtonDto;

	@IsOptional()
	shape?: LayerShapeDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerCTAOptionsDto)
	cta?: LayerCTAOptionsDto;

	@IsOptional()
	@IsString()
	assetPath?: string;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerAudioDto)
	audioConfig?: LayerAudioDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => InteractivityDto)
	interactivity?: InteractivityDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => ActionsDto)
	actions?: ActionsDto;

	@IsOptional()
	@IsObject()
	style?: any;

	@IsOptional()
	animation?: AnimationDto | AnimationDto[];

	@IsOptional()
	@ValidateNested()
	@Type(() => TimingConfigDto)
	timing?: TimingConfigDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationsDto)
	optimizations?: LayerOptimizationsDto;

	// Apply these optimizations on top of the original just for the preview window.
	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationsDto)
	previewOptimizations?: LayerOptimizationsDto;

	@IsOptional()
	@ValidateNested({ each: true })
	@IsArray()
	@Type(() => LayerDto)
	layers?: LayerDto[];

	@ValidateNested({ each: true })
	@IsArray()
	@IsOptional()
	@Type(() => VisibilityConditionDto)
	visibilityConditions?: VisibilityConditionDto[];

	@IsOptional()
	@IsBoolean()
	hideFromPreview?: boolean;

	@IsOptional()
	@IsBoolean()
	hideFromExport?: boolean;

	@IsOptional()
	@IsObject()
	mergeTags?: Record<string, MergeTagDto[]>;

	@IsOptional()
	@IsBoolean()
	hidden?: boolean;

	@IsOptional()
	@IsBoolean()
	locked?: boolean;

	@IsOptional()
	@IsString()
	remoteId?: string; // Used for remote layers, e.g., from a CMS or external source.

	@IsOptional()
	@IsString()
	purpose?: TextLayerPurpose | ImageLayerPurpose | ButtonLayerPurpose;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerGroupDto)
	group?: LayerGroupDto;

	@IsOptional()
	cropData?: ImageCropData;

	@IsOptional()
	@IsBoolean()
	lockAspectRatio?: boolean;
}

export class LayerWithDomContextDto extends LayerDto {
	@IsObject()
	element: any; // HTMLElement

	@IsString()
	creativeUnitId: string; // The associated creative unit ID for this layer.

	@IsBoolean()
	@IsOptional()
	isUnitSpecific?: boolean;
}
